import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AdminService } from '../services/admin.service';
import { AuthService } from '../services/auth.service';

/**
 * Route guard for /admin/dashboard.
 *
 * IMPORTANT: this is a UX convenience, NOT the security boundary. A determined
 * user can bypass any client-side guard (devtools, edited bundle, direct API
 * calls). What actually protects the data is server-side:
 *   • firestore.rules / storage.rules  → require the signed `admin` custom
 *     claim AND a second-factor sign-in on every read/write.
 *   • Cloud Functions / Django         → verify the ID token (and claim).
 * The guard re-reads those same signed claims so a non-admin never even sees
 * the dashboard shell.
 */
export const adminGuard: CanActivateFn = async () => {
  const adminService = inject(AdminService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const user = authService.getCurrentUser();
  if (!user) {
    return router.createUrlTree(['/admin/login']);
  }

  try {
    const status = await authService.getAdminStatus(user);
    if (!status.isAdmin || !status.hasSecondFactor) {
      return router.createUrlTree(['/admin/login']);
    }

    // Single-session enforcement: this tab must own a live session document.
    const sessionId = adminService.getCurrentSessionId();
    if (!sessionId || !(await adminService.isSessionValid(sessionId))) {
      return router.createUrlTree(['/admin/login']);
    }
    return true;
  } catch (error) {
    console.error('[AdminGuard] check failed:', error);
    return router.createUrlTree(['/admin/login']);
  }
};
